import {
  Match,
  TournamentCivAbrr,
  TournamentCivStats,
  TournamentMapStats,
  TournamentPlayerStats,
  TOURNAMENT_CIV_ABBRS,
  TOURNAMENT_DRAFT_CIVS,
} from "../types/tournaments";

function getOrInitPlayer(players: TournamentPlayerStats[], player: string) {
  const p = players.find((p) => p.name == player);
  if (p) return p;

  players.push({
    name: player,
    wins: 0,
    losses: 0,
    winrate: 0,
    civs: TOURNAMENT_CIV_ABBRS.reduce((acc, c) => {
      acc[c] = { wins: 0, losses: 0, played: 0 };
      return acc;
    }, {} as TournamentPlayerStats["civs"]),
  });
  return players[players.length - 1];
}

function getOrInitMap(maps: TournamentMapStats[], name: string) {
  const lcName = name.toLowerCase();
  const map = maps.find((m) => m.map.toLowerCase() == lcName);
  if (map) return map;
  maps.push({
    map: name,
    played: 0,
    bans: 0,
    civs: TOURNAMENT_CIV_ABBRS.reduce((acc, c) => {
      acc[c] = { wins: 0, losses: 0, played: 0 };
      return acc;
    }, {} as TournamentMapStats["civs"]),
  });
  return maps[maps.length - 1];
}

export function getTournamentStats(matches: Match[]) {
  const civStats = TOURNAMENT_CIV_ABBRS.reduce((acc, civ) => {
    acc[civ] = {
      civ,
      wins: 0,
      losses: 0,
      played: 0,
      mirrors: 0,
      winrate: 0,
      bans: 0,
    };
    return acc;
  }, {} as Record<TournamentCivAbrr, TournamentCivStats>);

  const maps: TournamentMapStats[] = [];
  const teams: TournamentPlayerStats[] = [];
  const players: TournamentPlayerStats[] = [];
  let hasTeams = false;

  for (const match of matches) {
    for (const game of match.games) {
      if (!game.played) continue;
      try {
        const winner = game.teams.find((t) => t.win);
        const loser = game.teams.find((t) => !t.win);
        if (!winner || !loser || !winner.civs || !loser.civs) continue;
        const winnerCivs = [winner.civs].flat();
        const loserCivs = [loser.civs].flat();
        if (winnerCivs.length == 1 && winnerCivs[0] == loserCivs[0]) civStats[winnerCivs[0]].mirrors++;

        winnerCivs.forEach(civ => {
          civStats[civ].wins++;
          civStats[civ].played++;
        });
        loserCivs.forEach(civ => {
          civStats[civ].losses++;
          civStats[civ].played++;
        });

        {
          const w = getOrInitPlayer(teams, winner.name);
          const l = getOrInitPlayer(teams, loser.name);

          winnerCivs.forEach(civ => {
            w.civs[civ].wins++;
            w.civs[civ].played++;
          });
          loserCivs.forEach(civ => {
            l.civs[civ].losses++;
            l.civs[civ].played++;
          });
        }

        if (winner.players.length > 1 || loser.players.length > 1)
          hasTeams = true;

        winner.players.forEach(player => {
          const w = getOrInitPlayer(players, player);
          winnerCivs.forEach(civ => {
            w.civs[civ].wins++;
            w.civs[civ].played++;
          });
        });

        loser.players.forEach(player => {
          const l = getOrInitPlayer(players, player);
          loserCivs.forEach(civ => {
            l.civs[civ].losses++;
            l.civs[civ].played++;
          });
        });

        const m = getOrInitMap(maps, game.map);
        winnerCivs.forEach(civ => {
          m.civs[civ].wins++;
          m.civs[civ].played++;
        });
        loserCivs.forEach(civ => {
          m.civs[civ].losses++;
          m.civs[civ].played++;
        });
        m.played++;
      } catch (e) {
        console.error("Error parsing stats", e, game);
      }
    }

    if (match.map_bans) {
      try {
        for (const map of match.map_bans) {
          const m = getOrInitMap(maps, map);
          m.bans++;
        }
      } catch (e) {
        console.error("Error parsing map_bans", e, match);
      }
    }

    if (match.civ_bans) {
      try {
        for (const civ of match.civ_bans) {
          civStats[TOURNAMENT_DRAFT_CIVS[civ]].bans++;
        }
      } catch (e) {
        console.error("Error parsing civ_bans", e, match);
      }
    }
  }

  teams.forEach((p) =>
    Object.values(p.civs).forEach((c) => {
      p.wins += c.wins;
      p.losses += c.losses;
    })
  );

  players.forEach((p) =>
    Object.values(p.civs).forEach((c) => {
      p.wins += c.wins;
      p.losses += c.losses;
    })
  );

  Object.values(civStats).forEach((c) => {
    c.winrate = c.played ? c.wins / c.played : 0.5;
  });

  const civs = Object.values(civStats).sort((a, b) => b.winrate - a.winrate);
  teams.forEach((p) => (p.winrate = p.wins / (p.wins + p.losses)));
  teams.sort((a, b) => a.losses - b.losses).sort((a, b) => b.wins - a.wins);
  players.forEach((p) => (p.winrate = p.wins / (p.wins + p.losses)));
  players.sort((a, b) => a.losses - b.losses).sort((a, b) => b.wins - a.wins);
  maps.sort((a, b) => b.played - a.played);

  return { civs, teams, players, maps, hasTeams };
}
